import type { Platform } from '../platform/types';
import type { ImportMethod } from '../db/schema';
import { crystallise } from './crystallise';
import { extract } from './extract';
import { isBotWall } from './wall';

/**
 * Fetch → read → save, with progress. Every step fails forward except the
 * fetch: the user always ends up with an item, and the item was created before
 * this ran.
 */

export type Step = 'fetch' | 'read' | 'save';
export type StepState = 'waiting' | 'running' | 'done' | 'failed';

export type Progress = {
  steps: Record<Step, StepState>;
  /** Only while saving. Drives "Saving photos… 3 of 7". */
  saved: { done: number; total: number } | undefined;
};

export const STARTING: Progress = {
  steps: { fetch: 'waiting', read: 'waiting', save: 'waiting' },
  saved: undefined,
};

export type ImportResult = {
  finalUrl: string;
  method: ImportMethod;
  title: string | undefined;
  description: string;
  price: number | undefined;
  currency: string | undefined;
  imageIds: string[];
  /** Offered by the page but unsaveable. Surfaced, never fatal. */
  failedImages: number;
};

/**
 * The page couldn't be read at all. The one failure the user has to know
 * about, because the answer to it is "paste the page text instead".
 */
export class FetchBlockedError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'FetchBlockedError';
    this.status = status;
  }
}

export async function runImport({
  platform,
  url,
  html,
  onProgress,
}: {
  platform: Platform;
  url: string;
  /** Supplied by the paste-instead fallback, which skips the fetch. */
  html?: string | undefined;
  onProgress: (progress: Progress) => void;
}): Promise<ImportResult> {
  const progress: Progress = {
    steps: { ...STARTING.steps },
    saved: undefined,
  };

  const report = (step: Step, state: StepState) => {
    progress.steps[step] = state;
    onProgress({ steps: { ...progress.steps }, saved: progress.saved });
  };

  // 1. Fetch.
  let source: { html: string; finalUrl: string };
  if (html === undefined) {
    report('fetch', 'running');
    try {
      source = await fetchPage(platform, url);
    } catch (thrown) {
      report('fetch', 'failed');
      throw thrown;
    }
    report('fetch', 'done');
  } else {
    // Nothing was fetched, so the step is neither running nor failed — the
    // indicator shows it as already settled.
    source = { html, finalUrl: url };
    report('fetch', 'done');
  }

  // 2. Read. Parsing can't really fail — a page with nothing in it produces an
  // Extracted full of undefined, which is a legitimate answer.
  report('read', 'running');
  const found = extract(source.html, source.finalUrl);
  report('read', 'done');

  // 3. Save. The step that makes the item survive the listing.
  report('save', 'running');
  const saved = await crystallise(platform, found.images, source.finalUrl, (done, total) => {
    progress.saved = { done, total };
    onProgress({ steps: { ...progress.steps }, saved: progress.saved });
  });
  progress.saved = undefined;
  report('save', 'done');

  return {
    finalUrl: source.finalUrl,
    method: found.method,
    title: found.title,
    description: found.description,
    price: found.price,
    currency: found.currency,
    imageIds: saved.imageIds,
    failedImages: saved.failed,
  };
}

/**
 * Amazon serves its interstitial to a fraction of requests and the listing to
 * the rest, so one unlucky response shouldn't cost the import. One retry,
 * once, after a pause — enough for the common case, and not a loop that hammers
 * a shop that has genuinely decided to say no.
 */
const RETRY_AFTER_MS = 2500;

async function fetchPage(
  platform: Platform,
  url: string,
): Promise<{ html: string; finalUrl: string }> {
  let page = await platform.fetchUrl(url);

  if (page.status < 400 && isBotWall(page.html)) {
    await new Promise((resume) => setTimeout(resume, RETRY_AFTER_MS));
    page = await platform.fetchUrl(url);
  }

  if (page.status >= 400) {
    throw new FetchBlockedError(reasonFor(page.status), page.status);
  }

  // A challenge page is a 200 with nothing on it. Caught here rather than
  // downstream, because by the time the extractors have run it looks exactly
  // like a shop that publishes no details — and an item named after the shop,
  // reported as imported, is worse than a failure the user can see.
  if (isBotWall(page.html)) {
    throw new FetchBlockedError('That shop served a “are you a robot” page instead of the listing.');
  }

  return { html: page.html, finalUrl: page.finalUrl };
}

function reasonFor(status: number): string {
  if (status === 403 || status === 401) {
    return "That shop blocked the request — it's checking for browsers.";
  }
  if (status === 404) return "That page doesn't exist any more.";
  if (status === 429) return 'That shop is rate-limiting us. Try again shortly.';
  if (status >= 500) return 'That shop’s server is having trouble.';
  return `That page replied ${status}.`;
}

/**
 * What someone actually pastes. A bare "westelm.com/products/andes" is a URL
 * to a person, and making them type the scheme is a papercut.
 *
 * Returns undefined when there's no reading of the input that's a web address.
 */
export function normaliseUrl(input: string): string | undefined {
  const trimmed = input.trim();
  if (trimmed === '') return undefined;

  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(withScheme);
    // A hostname with no dot is a typo, not a site.
    return parsed.hostname.includes('.') ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}
